import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppConfig, SessionManagementConfig } from '@config/app.config';
import { LocalStorageService } from '@services/localStorage/local-storage.service';
import { TranslationMWService } from '@services/translation-middleware/translation-middleware.service';
import { KeepSessionOpenDialogComponent } from '@shared/components/dialogs/keep-session-open-dialog/keep-session-open-dialog.component';
import { offsetDate } from '@utils/utils';
import { ToastrService } from 'ngx-toastr';

// Must return a function that returns void | Promise<any> | Observable<any>
export const sessionManagerInitializerFactory = (injector: Injector) => () => {
  const sessionManager = injector.get(SessionManagerService);
  return sessionManager.init();
};

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService implements SessionManagementConfig {
  // configuration properties from SessionManagementConfig
  public readonly timeUnit: 'hours' | 'minutes' | 'seconds';
  public readonly checkOutTime: number;
  public readonly maxInactivity: number;
  public readonly confirmationWait: number;
  public readonly maxPersistentDays: number;
  // share the instance to avoid excessive inheritance
  private static instance$: SessionManagerService;
  // inject ToastService
  private toastr$: ToastrService;

  private counting = false;
  private dialoguing = false;

  constructor(
    private injector: Injector,
    private dialog: MatDialog,
    private router: Router,
    private translationMWService: TranslationMWService,
    private localStorageService: LocalStorageService
  ) {
    this.timeUnit = AppConfig.SESSION_MANAGEMENT.timeUnit;
    this.checkOutTime = AppConfig.SESSION_MANAGEMENT.checkOutTime;
    this.maxInactivity = AppConfig.SESSION_MANAGEMENT.maxInactivity;
    this.confirmationWait = AppConfig.SESSION_MANAGEMENT.confirmationWait;
    this.maxPersistentDays = AppConfig.SESSION_MANAGEMENT.maxPersistentDays;
  }

  public static get INSTANCE(): SessionManagerService {
    return SessionManagerService.instance$;
  }

  private get toastr(): ToastrService {
    if (!this.toastr$) {
      this.toastr$ = this.injector.get(ToastrService);
    }
    return this.toastr$;
  }

  /**
   * Called once at app startup via APP_INITIALIZER.
   * Angular will not finish bootstrapping until this Promise resolves.
   */
  init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      SessionManagerService.instance$ = this;
      resolve();
    });
  }

  private get nextTimeOut(): number {
    const multiplier = this.timeUnit === 'hours' ? 60 * 60 : this.timeUnit === 'minutes' ? 60 : 1;
    return this.checkOutTime * multiplier * 1000;
  }

  private get confirmationWaitSeconds(): number {
    const multiplier = this.timeUnit === 'hours' ? 60 * 60 : this.timeUnit === 'minutes' ? 60 : 1;
    return this.confirmationWait * multiplier;
  }

  private logout() {
    this.counting = false;
    this.localStorageService.clear();
    void this.router.navigate(['/auth/login']);
  }

  private get tokenVsLoginDaysDiff(): number {
    const userLastLoginAt = offsetDate(this.localStorageService.lastLoginAt)
      .toISOString()
      .slice(0, 10);
    const tokenIssuedAt = new Date(this.localStorageService.tokenIssuedAt * 1000)
      .toISOString()
      .slice(0, 10);
    // get number of days between token issued at and user last login at
    const daysDiff = Math.floor(
      Math.abs(new Date(userLastLoginAt).getTime() - new Date(tokenIssuedAt).getTime()) /
        (1000 * 60 * 60 * 24)
    );

    return daysDiff;
  }

  private timeOutHandler = () => {
    if (!this.localStorageService.userIsAuthenticated) {
      // stop counting if user is not authenticated
      this.counting = false;
    } else if (this.tokenVsLoginDaysDiff > 0) {
      // log user out after midnight
      this.logout();
    }
    if (this.counting) {
      this.localStorageService.inactivityTime += this.checkOutTime;
      // console.log(
      //   `SessionManagerService::timeOutHandler (${this.timeUnit}):`,
      //   this.localStorageService.inactivityTime
      // );
      // ask user for more time
      if (this.localStorageService.inactivityTime >= this.maxInactivity) {
        if (this.localStorageService.sessionIsPersistent) {
          // keep persistent sessions open for 90 days
        } else {
          if (!this.dialoguing) {
            this.dialoguing = true;
            // request user to extend session for a while
            this.dialog
              .open(KeepSessionOpenDialogComponent, {
                disableClose: true,
                data: {
                  timeOut: this.confirmationWaitSeconds,
                  checkOutTime: this.checkOutTime,
                },
              })
              .afterClosed()
              .subscribe((confirmed: boolean) => {
                this.dialoguing = false;
                if (confirmed) {
                  this.resetInactivity();
                } else {
                  this.logout();
                }
              });
          } else {
            if (
              this.localStorageService.inactivityTime >=
              this.maxInactivity + this.confirmationWait
            ) {
              this.toastr.error(
                this.translationMWService.instant('session-manager-service.toastr.logout.message'),
                this.translationMWService.instant('session-manager-service.toastr.logout.title')
              );
              this.dialog.closeAll();
              this.logout();
            }
          }
        }
      }
      setTimeout(this.timeOutHandler, this.nextTimeOut);
    }
  };

  public setTimeout() {
    if (!this.localStorageService.userIsAuthenticated) {
      return;
    }
    if (this.localStorageService.sessionIsPersistent) {
      if (this.tokenVsLoginDaysDiff >= this.maxPersistentDays) {
        // log user out after midnight
        this.logout();
      }
    } else if (!this.counting) {
      this.counting = true;
      setTimeout(this.timeOutHandler, this.nextTimeOut);
    }
  }

  public resetInactivity() {
    this.localStorageService.inactivityTime = 0;
    // console.log('SessionManagerService:resetInactivity');
    this.setTimeout();
  }
}
